import { type JSX } from 'preact';
import { useState } from 'preact/hooks';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Alert,
} from '../../components';
import { reconstructSecret, cleanShareString } from '../../crypto';
import { navigateTo } from '../../App';

export function ReconstructSecretPage(): JSX.Element {
  const [shareInputs, setShareInputs] = useState<string[]>(['', '', '']);
  const [reconstructedSecret, setReconstructedSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleShareChange = (index: number, value: string): void => {
    const newShares = [...shareInputs];
    newShares[index] = value;
    setShareInputs(newShares);
  };

  const handleAddShare = (): void => {
    setShareInputs([...shareInputs, '']);
  };

  const handleRemoveShare = (index: number): void => {
    if (shareInputs.length > 2) {
      const newShares = shareInputs.filter((_, i) => i !== index);
      setShareInputs(newShares);
    }
  };

  const handleReconstruct = async (e: JSX.TargetedEvent<HTMLFormElement, Event>): Promise<void> => {
    e.preventDefault();
    setError(null);
    setReconstructedSecret(null);

    try {
      // Filter out empty shares and clean them
      const validShares = shareInputs
        .filter((share) => share.trim())
        .map((share) => cleanShareString(share));

      if (validShares.length < 2) {
        throw new Error('Please enter at least 2 shares');
      }

      // Reconstruct the secret
      const secret = await reconstructSecret(validShares);
      setReconstructedSecret(secret);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      setError(message);
    }
  };

  const handleReset = (): void => {
    setShareInputs(['', '', '']);
    setReconstructedSecret(null);
    setError(null);
    setCopied(false);
  };

  const handleCopySecret = (): void => {
    if (reconstructedSecret) {
      navigator.clipboard.writeText(reconstructedSecret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>Reconstruct Secret from Shares</CardTitle>
          <CardDescription>
            Enter the shares to reconstruct the original secret using Shamir Secret Sharing
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert type="info" title="Instructions" className="mb-6">
            <ul className="list-disc list-inside space-y-1">
              <li>Enter at least the threshold number of shares (e.g., 3 of 5)</li>
              <li>Shares can include spaces - they will be automatically removed</li>
              <li>Format: "1-ABCD..." where 1 is the share index</li>
              <li>All reconstruction happens in your browser</li>
            </ul>
            <p className="mt-3 font-semibold">
              <a
                href="/shamir/specification"
                onClick={(e) => {
                  e.preventDefault();
                  navigateTo('/shamir/specification');
                }}
                className="text-blue-600 hover:underline cursor-pointer"
              >
                📄 View Technical Specification
              </a>{' '}
              - Complete algorithm details for manual reconstruction
            </p>
          </Alert>

          {error && (
            <Alert type="error" title="Error" className="mb-6">
              {error}
            </Alert>
          )}

          {reconstructedSecret ? (
            <div className="space-y-4">
              <Alert type="success" title="Secret Reconstructed!" className="mb-6">
                <p>Your secret has been successfully reconstructed from the provided shares.</p>
              </Alert>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Reconstructed Secret
                </label>
                <div className="relative">
                  <textarea
                    value={reconstructedSecret}
                    readOnly
                    className="w-full px-3 py-2 border border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-900/20 rounded-md text-gray-900 dark:text-gray-100 font-mono"
                    rows={8}
                  />
                  <Button
                    size="sm"
                    onClick={handleCopySecret}
                    className="absolute top-2 right-2"
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>

              <Alert type="warning" title="Security Notice" className="mt-6">
                <ul className="list-disc list-inside space-y-1">
                  <li>Clear your browser history to remove the secret from memory</li>
                  <li>Close this page when done to clear the secret</li>
                  <li>Be aware that anyone with threshold shares can reconstruct the secret</li>
                </ul>
              </Alert>

              <div className="flex gap-2 justify-end pt-4">
                <Button onClick={handleReset} size="lg">
                  Reconstruct Another Secret
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleReconstruct} className="space-y-4">
              <div className="space-y-3">
                {shareInputs.map((share, index) => (
                  <div key={index} className="flex gap-2">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Share {index + 1}
                      </label>
                      <input
                        type="text"
                        value={share}
                        onChange={(e) => handleShareChange(index, e.target.value)}
                        placeholder={`e.g., ${index + 1}-ABCD2EFG...`}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                      />
                    </div>
                    {shareInputs.length > 2 && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => handleRemoveShare(index)}
                        className="mt-7"
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex gap-2">
                <Button type="button" variant="secondary" onClick={handleAddShare}>
                  Add Another Share
                </Button>
              </div>

              <div className="flex gap-2 justify-end pt-4 border-t border-gray-200">
                <Button type="submit" size="lg">
                  Reconstruct Secret
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
